/**
 * Manual Fibonacci Time projections from two clicked points.
 *
 * The user picks two pivots; the bars between them are the unit, and Fibonacci multiples of that
 * unit are projected forward from the SECOND point:
 *
 *   P1        P2
 *   |----D----|--0.382D--|---0.618D---|-----1.0D-----|
 *             ^ origin   ^ target     ^ target       ^ target
 *
 * From P2, not P1 — the interval has already played out once by the time it is drawn, so the
 * question is when the next multiple of it falls. This differs from the automatic Recent Fibs
 * tool, which projects from its more recent anchor for the same reason but arrives at a different
 * origin because it picks its own pair.
 */

/** Ratios projected forward from P2. */
export const FIB_TIME_RATIOS = [0.236, 0.382, 0.5, 0.618, 0.786, 1.0, 1.272, 1.618, 2.0, 2.618, 4.236];

/** Ratio formatted for a label: 0.618, 1, 2.618 — trailing zeros trimmed. */
export function formatRatio(ratio) {
  if (typeof ratio !== 'number' || !Number.isFinite(ratio)) return '';
  return String(parseFloat(ratio.toFixed(3)));
}

/**
 * Target bar indices, projected from P2.
 *
 * D is signed: `p2 - p1`. Clicking right-to-left gives a negative interval and the targets march
 * LEFT from P2, which is the honest reading of "one interval further on" when the interval itself
 * runs backwards. Taking the absolute value would silently turn a backwards selection into a
 * forwards projection and quietly ignore what the user actually clicked.
 *
 * Indices are floored, so a fractional bar never appears; with a negative interval flooring moves
 * the target further from P2 rather than nearer, which keeps the spacing monotonic in the
 * direction of travel.
 *
 * @param {number} p1Index bar index of the first click
 * @param {number} p2Index bar index of the second click
 * @param {object} [opts]
 * @param {number} [opts.maxIndex]  last addressable bar; targets beyond it are dropped
 * @returns {Array<{ratio:number, index:number, label:string}>} in projection order
 */
export function fibTimeTargets(p1Index, p2Index, { maxIndex = Infinity, ratios = FIB_TIME_RATIOS } = {}) {
  const usable = (n) => typeof n === 'number' && Number.isFinite(n);
  if (!usable(p1Index) || !usable(p2Index)) return [];

  const distance = p2Index - p1Index;
  // Two clicks on the same bar define no interval; every target would land on P2.
  if (distance === 0) return [];

  const out = [];
  ratios.forEach(ratio => {
    const index = p2Index + Math.floor(distance * ratio);
    // Off the addressable chart in either direction: there is no bar to draw on, and emitting it
    // would have the renderer drop it while the caller still counted it as drawn.
    if (index < 0 || index > maxIndex) return;
    out.push({ ratio, index, label: `Fib ${formatRatio(ratio)}` });
  });
  return out;
}

/**
 * The overlays for one Fib Time drawing.
 *
 * Every line shares a groupId so the whole projection selects, deletes and undoes as one figure
 * rather than as eleven unrelated verticals.
 *
 * @param {object} p1     {index}
 * @param {object} p2     {index}
 * @param {string[]} dates  chart dates, including any padded future space
 * @param {object} [opts] {color, groupId, lastRealIndex}
 * @returns {Array} vLine overlays, empty when the selection projects nothing
 */
export function buildFibTimeOverlays(p1, p2, dates, { color = '#7b1fa2', groupId, lastRealIndex = null } = {}) {
  if (!p1 || !p2 || !Array.isArray(dates) || dates.length === 0) return [];

  const targets = fibTimeTargets(p1.index, p2.index, { maxIndex: dates.length - 1 });
  if (targets.length === 0) return [];

  const gid = groupId || `fibtime-${p1.index}-${p2.index}`;
  const overlays = [];

  // The two clicked points, so the interval being multiplied is visible rather than inferred from
  // the spacing of the projections.
  [[p1, 'P1'], [p2, 'P2']].forEach(([p, name]) => {
    if (p.index < 0 || p.index >= dates.length) return;
    overlays.push({
      type: 'vLine',
      date: dates[p.index],
      color: '#455a64',
      lineType: 'solid',
      label: name,
      groupId: gid,
      id: `${gid}-anchor-${name}`,
    });
  });

  targets.forEach(t => {
    const isFuture = lastRealIndex !== null && t.index > lastRealIndex;
    overlays.push({
      type: 'vLine',
      date: dates[t.index],
      color,
      lineType: 'dashed',
      // The date is on the label because the whole point of a time projection is WHEN, and a
      // vertical line in empty space to the right of the price has no axis reading to fall back on.
      label: `${t.label}\n${dates[t.index]}`,
      isFuture,
      groupId: gid,
      id: `${gid}-t-${t.ratio}`,
    });
  });

  return overlays;
}
